import React, { useCallback, useEffect, useState } from 'react';
import { answerStaffConflictPrompt } from '../../utils/staffConflictPrompt';

const REASON_LABEL = {
    assigned: 'inne wydarzenie tego dnia',
    unavailable: 'niedostępny/a',
};

export default function StaffConflictPromptHost() {
    const [prompt, setPrompt] = useState(null);

    const close = useCallback(() => {
        setPrompt(null);
    }, []);

    useEffect(() => {
        const onPrompt = (e) => {
            const detail = e?.detail || {};
            if (detail.mode === 'info') {
                const token = Date.now();
                setPrompt({ ...detail, mode: 'info', _token: token });
                const ms = Number(detail.durationMs) || 4000;
                window.setTimeout(() => {
                    setPrompt((cur) => (cur?._token === token ? null : cur));
                }, ms);
                return;
            }
            setPrompt({ ...detail, mode: 'confirm' });
        };
        window.addEventListener('planner:staffConflictPrompt', onPrompt);
        return () => window.removeEventListener('planner:staffConflictPrompt', onPrompt);
    }, []);

    if (!prompt) return null;

    const isConfirm = prompt.mode === 'confirm';
    const items = Array.isArray(prompt.items) ? prompt.items : [];

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-[10001] flex justify-center px-4 pb-4 pointer-events-none"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
            <div
                role={isConfirm ? 'dialog' : 'status'}
                aria-live={isConfirm ? 'assertive' : 'polite'}
                aria-labelledby="staff-conflict-prompt-title"
                className="pointer-events-auto w-full max-w-lg rounded-xl shadow-2xl border
                    border-amber-300/80 dark:border-amber-600/80
                    bg-amber-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100
                    animate-[slideUp_0.22s_ease-out]"
            >
                <div className="px-4 pt-4 pb-3">
                    <p id="staff-conflict-prompt-title" className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                        {prompt.title || 'Konflikt zajętości'}
                    </p>
                    {prompt.message && (
                        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{prompt.message}</p>
                    )}
                    {items.length > 0 && (
                        <ul className="mt-2 space-y-1 text-sm">
                            {items.map((item) => (
                                <li
                                    key={`${item.name}-${item.reason || 'x'}`}
                                    className="flex items-start gap-2 text-gray-800 dark:text-gray-200"
                                >
                                    <span className="text-amber-600 dark:text-amber-400 mt-0.5">•</span>
                                    <span>
                                        <span className="font-medium">{item.name}</span>
                                        {item.reason && (
                                            <span className="text-gray-600 dark:text-gray-400">
                                                {' '}({REASON_LABEL[item.reason] || item.reason})
                                            </span>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {isConfirm && (
                        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                            {prompt.confirmQuestion
                                || `Czy na pewno chcesz przypisać ${items.length > 1 ? 'te osoby' : 'tę osobę'}?`}
                        </p>
                    )}
                </div>

                {isConfirm ? (
                    <div className="flex gap-2 px-4 pb-4">
                        <button
                            type="button"
                            onClick={() => {
                                answerStaffConflictPrompt(false);
                                close();
                            }}
                            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium
                                bg-gray-200 hover:bg-gray-300 text-gray-900
                                dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-100"
                        >
                            Anuluj
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                answerStaffConflictPrompt(true);
                                close();
                            }}
                            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium
                                bg-amber-600 hover:bg-amber-700 text-white"
                        >
                            Potwierdzam
                        </button>
                    </div>
                ) : (
                    <div className="px-4 pb-3 flex justify-end">
                        <button
                            type="button"
                            onClick={close}
                            className="px-3 py-1.5 rounded-lg text-sm
                                bg-amber-200/80 hover:bg-amber-300/80 text-amber-950
                                dark:bg-amber-900/50 dark:hover:bg-amber-800/60 dark:text-amber-100"
                        >
                            OK
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(1rem); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}
